import { Check } from 'lucide-react';

import type { ComponentProps, JSX } from 'react';

import { cn, type TIcon } from '~/utils';
import { Show } from '~/components/show';
import { Label } from '~/components/label';
import { Text } from '~/components/text';
import { HStack, Stack } from '~/components/stack';
import { RadioGroup, RadioGroupItem } from '~/components/radio-group';

export interface IRadioCardDataProps {
  icon?: TIcon;
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface IRadioCardProps extends ComponentProps<typeof RadioGroup> {
  items: IRadioCardDataProps[];
}

export function RadioCard({
  items,
  className,
  ...props
}: IRadioCardProps): JSX.Element {
  return (
    <RadioGroup className={cn('flex flex-row gap-2', className)} {...props}>
      {items.map(({ value, label, icon, description, disabled }) => {
        return (
          <RadioCardItem
            id={value}
            key={value}
            icon={icon}
            label={label}
            value={value}
            description={description}
            disabled={disabled}
            checked={props.value === value}
          />
        );
      })}
    </RadioGroup>
  );
}

interface IRadioCardItemProps extends ComponentProps<typeof RadioGroupItem> {
  icon?: TIcon;
  label?: string;
  description?: string;
  disabled?: boolean;
}

export function RadioCardItem({
  icon,
  value,
  label,
  checked,
  className,
  description,
  disabled,
  ...props
}: IRadioCardItemProps): JSX.Element {
  const Icon = icon as TIcon;

  return (
    <div
      data-checked={checked}
      className={cn(
        'flex items-center flex-1 bg-[#f2f3f5] hover:bg-[#f6f7f8] data-[checked=true]:bg-[#effff5] data-[checked=true]:ring-1 data-[checked=true]:ring-illicitGreen-500 gap-2.5 rounded-md min-w-[155px]',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <RadioGroupItem
        id={value}
        value={value}
        checked={checked}
        disabled={disabled}
        className={cn('opacity-0 absolute', className)}
        {...props}
      />
      <Label
        htmlFor={value}
        className={cn(
          'font-medium px-4 py-3 w-full cursor-pointer',
          disabled && 'cursor-not-allowed text-gray-400',
        )}
      >
        <Stack gap={2} className='w-full'>
          <Show when={icon}>
            <HStack>
              <div className='flex-1'>
                <Icon className='size-4' />
              </div>
              <Show when={checked}>
                <Check className='size-4' />
              </Show>
            </HStack>
          </Show>
          {label}
          <Show when={description}>
            <Text size='sm'>{description}</Text>
          </Show>
        </Stack>
        <Show when={!icon}>
          <div className='size-4'>
            <Show when={checked}>
              <Check className='size-4' />
            </Show>
          </div>
        </Show>
      </Label>
    </div>
  );
}
